<script setup>
import { Link, router } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import Pagination from '@/Components/Pagination.vue';
import { ref, watch, onMounted } from "vue";
import debounce from "lodash/debounce";
import { useToast } from 'vue-toast-notification';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import Loading from 'vue-loading-overlay';

const props = defineProps({
    year: String,
    allBookings: Object,
    filters: Object,
    flash: Object,
    user: Object,
});

let bookingSearch = ref(props.filters.bookingSearch);
const $toast = useToast();
dayjs.extend(advancedFormat);
let isLoading = ref(false);

watch(bookingSearch, debounce(function (value) {
    router.get('/bookings', { bookingSearch: value }, {
        preserveState: true,
        replace: true,
        onStart: () => isLoading.value = true,
        onFinish: () => isLoading.value = false,
    });
}, 700));

onMounted(() => {
    if (props.flash.message) {
        $toast.open({
            message: props.flash.message,
            type: "info",
            duration: 4000,
            position: 'top-right',
            class: 'bs-toast toast bg-dark show'
        });
    }
});

const getInitials = (name) => {
    let rgx = new RegExp(/(\p{L}{1})\p{L}+/, 'gu');
    let initials = [...name.matchAll(rgx)] || [];
    initials = (
        (initials.shift()?.[1] || '') + (initials.pop()?.[1] || '')
    ).toUpperCase();
    return initials;
};
</script>
<style scoped>
@import "../../../../public/ui/vendor/libs/toastr/toastr.css";
@import "vue-loading-overlay/dist/vue-loading.css";
</style>
<template>
    <AppLayout title="Bookings Manager" :year=year :user=user>
        <div class="vld-parent">
            <Loading v-model:active="isLoading" :color="'#696cff'" />
        </div>
        <div class="container-xxl flex-grow-1 container-p-y">
            <div class="card">
                <div class="card-datatable table-responsive">
                    <div id="DataTables_Table_0_wrapper" class="dataTables_wrapper dt-bootstrap5 no-footer">
                        <div class="card-header flex-column flex-md-row">
                            <div class="head-label text-center">
                                <h5 class="card-title mb-0">Bookings Manager</h5>
                            </div>
                            <div class="dt-action-buttons text-end pt-3 pt-md-0">
                                <div class="dt-buttons" v-if="$page.props.user.permissions.includes('add-bookings')">
                                    <Link type="button" class="btn btn-primary" href="/bookings/create/" preserve-scroll>
                                    <i class='bx bx-plus bx-tada'></i> Add New Booking
                                    </Link>
                                </div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-sm-12 col-md-12 d-flex justify-content-center justify-content-md-end">
                                <div class="dataTables_filter">
                                    <label>Search:
                                        <input v-model="bookingSearch" type="text" class="form-control"
                                            placeholder="Type Booking Number"></label>
                                </div>
                            </div>
                        </div>
                        <table class="datatables-basic table border-top dataTable no-footer dtr-column"
                            id="DataTables_Table_0" role="grid" aria-describedby="DataTables_Table_0_info"
                            style="width: 1390px;">
                            <thead>
                                <tr role="row">
                                    <th>Booking Number</th>
                                    <th>Hotel Name</th>
                                    <th>Arrival Date</th>
                                    <th>Last Updated</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr class="odd" v-for="(booking, index) in allBookings.data" :key="booking.id">
                                    <td>
                                        <div class="d-flex justify-content-start align-items-center user-name">
                                            <div class="avatar-wrapper">
                                                <div class="avatar me-2"><span
                                                        class="avatar-initial rounded-circle bg-label-warning">{{
                                                            getInitials(booking.hotel.name)
                                                        }}</span>
                                                </div>
                                            </div>
                                            <div class=" d-flex flex-column">
                                                <span class="emp_name text-truncate">{{
                                                    booking.booking_no
                                                }}</span>
                                                <small v-if="booking.stay_type == 0"
                                                    class="emp_post text-truncate text-muted">
                                                    Full Stay
                                                </small>
                                                <small v-if="booking.stay_type == 1"
                                                    class="emp_post text-truncate text-muted">
                                                    Split Stay
                                                </small>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        {{ booking.hotel.name }}
                                    </td>
                                    <td>{{ dayjs(booking.arrival_date).format('Do MMMM YYYY') }}
                                    </td>
                                    <td>{{ dayjs(booking.updated_at).format('Do MMMM YYYY') }}</td>
                                    <td>
                                        <span v-if="booking.status == 'Pending'" class="badge bg-label-dark">Pending</span>
                                        <span v-if="booking.status == 'SendConfirmation'"
                                            class="badge bg-label-warning">Send to Confirmation</span>
                                        <span v-if="booking.status == 'Confirmed'"
                                            class="badge bg-label-info">Confirmed</span>
                                        <span v-if="booking.status == 'Cancelled'"
                                            class="badge bg-label-danger">Cancelled</span>
                                        <span v-if="booking.status == 'Paid'" class="badge bg-label-success">Paid</span>
                                    </td>
                                    <td>
                                        <Link :href="'/bookings/show/' + booking.id"
                                            v-if="$page.props.user.permissions.includes('booking-manager')"
                                            class="btn btn-icon btn-label-secondary" preserve-state>
                                        <i class="bx bx-search-alt"></i>
                                        </Link>
                                        <Link :href="'/bookings/edit/' + booking.id"
                                            v-if="$page.props.user.permissions.includes('edit-bookings')"
                                            class="btn btn-icon btn-label-secondary m-1" preserve-state>
                                        <i class="bx bx-edit"></i>
                                        </Link>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div class="row">
                            <div class="col-sm-12 col-md-6">
                                <div class="dataTables_info" id="DataTables_Table_0_info" role="status" aria-live="polite">
                                    Showing {{ allBookings.from }} to {{ allBookings.to }} of {{
                                        allBookings.total
                                    }} Bookings</div>
                            </div>
                            <div class="col-sm-12 col-md-6">
                                <div class="dataTables_paginate">
                                    <Pagination :links="allBookings.links"></Pagination>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </AppLayout>
</template>